import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { EVENTS_DATA, type EventData } from "../lib/events-data";
import { useAuth } from "../lib/auth-context";
import { api } from "@repo/api-client";

// ── Ticket tiers for Astral Healing retreat ─────────────────────────
interface TicketTier {
  id: string;
  name: string;
  price: number | "pwyc";
  description: string;
  perks: string[];
  available: boolean;
  highlight?: boolean;
}

const ASTRAL_TICKETS: TicketTier[] = [
  {
    id: "pwyc",
    name: "Pay What You Can",
    price: "pwyc",
    description: "For seekers where cost is a barrier. Contribute what feels right.",
    perks: [
      "Full retreat access",
      "Meals included",
      "Community satsang",
    ],
    available: true,
  },
  {
    id: "devotee",
    name: "Devotee",
    price: 108,
    description: "The standard path — full access to all sessions and sacred space.",
    perks: [
      "Full retreat access",
      "Meals included",
      "All workshops &amp; satsangs",
      "Retreat booklet",
    ],
    available: true,
    highlight: true,
  },
  {
    id: "sena-circle",
    name: "Sena Circle",
    price: 222,
    description: "Deepened experience with priority seating and teacher access.",
    perks: [
      "Full retreat access",
      "Meals included",
      "All workshops &amp; satsangs",
      "Priority seating",
      "Teacher Q&amp;A sessions",
      "Retreat booklet + gift",
    ],
    available: true,
  },
  {
    id: "enlightenment-pass",
    name: "Enlightenment Pass",
    price: 378,
    description: "The complete journey — all perks plus private consultation.",
    perks: [
      "Full retreat access",
      "Meals included",
      "All workshops &amp; satsangs",
      "Priority seating",
      "Teacher Q&amp;A sessions",
      "Private 30-min consultation",
      "Sacred gift bundle",
      "Retreat booklet",
    ],
    available: true,
  },
];

// ── Sponsor tiers ───────────────────────────────────────────────────
interface SponsorTier {
  name: string;
  price: number;
  perks: string[];
  emoji: string;
}

const SPONSOR_TIERS: SponsorTier[] = [
  {
    name: "Supporter",
    price: 500,
    emoji: "🌱",
    perks: ["Name in event program", "2 Devotee tickets"],
  },
  {
    name: "Patron",
    price: 1000,
    emoji: "🌟",
    perks: ["Logo on event materials", "4 Devotee tickets", "Social media mention"],
  },
  {
    name: "Visionary",
    price: 2500,
    emoji: "🔮",
    perks: [
      "Premium logo placement",
      "6 Sena Circle tickets",
      "Speaking opportunity",
      "Social media feature",
    ],
  },
];

export function EventDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();

  const staticEvent = EVENTS_DATA.find((e) => e.slug === slug);
  const [event, setEvent] = useState<EventData | undefined>(staticEvent);
  const [tickets, setTickets] = useState<TicketTier[]>(
    slug?.includes("astral") ? ASTRAL_TICKETS : []
  );

  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [registrationStep, setRegistrationStep] = useState<"tiers" | "form" | "confirm">("tiers");
  const [formData, setFormData] = useState({ name: "", email: "", dietary: "" });
  const [pwycAmount, setPwycAmount] = useState("");
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.getEvent(slug).then((res) => {
      // Map API event to EventData shape
      setEvent((prev) => prev ? {
        ...prev,
        title: res.event.title,
        subtitle: res.event.subtitle ?? prev.subtitle,
        dateRange: res.event.date_start ? res.event.date_start.split("T")[0] : prev.dateRange,
        location: res.event.location_name ?? prev.location,
      } : prev);
      if (res.ticket_tiers && res.ticket_tiers.length > 0) {
        setTickets(res.ticket_tiers.map((t) => ({
          id: t.id,
          name: t.name,
          price: t.price_type === "pwyc" ? "pwyc" : t.price,
          description: t.description ?? "",
          perks: t.perks ?? [],
          available: t.active,
          highlight: false,
        })));
      }
    }).catch(() => {});
  }, [slug]);

  if (!event) {
    return <Navigate to="/events" replace />;
  }

  const selectedTicket = tickets.find((t) => t.id === selectedTier);

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    setRegistrationStep("confirm");
  }

  if (registered) {
    return (
      <div className="container" style={{ padding: "5rem 0", textAlign: "center" }}>
        <div className="card" style={{ maxWidth: "540px", margin: "0 auto", padding: "3rem 2rem" }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🎟️</div>
          <h1 className="display-3" style={{ marginBottom: "0.75rem" }}>You're Registered!</h1>
          <p className="body-lg text-muted">
            Welcome, <strong>{formData.name || user?.email}</strong>! Your spot at{" "}
            <strong>{event.title}</strong> is confirmed. Check your email for details.
          </p>
          <div className="event-confirm-details">
            <div>📅 {event.dateRange}</div>
            <div>📍 {event.location}, {event.locationDetail}</div>
            {selectedTicket && (
              <div>
                🎟️ {selectedTicket.name} —{" "}
                {selectedTicket.price === "pwyc"
                  ? `$${pwycAmount || "Your contribution"}`
                  : `$${selectedTicket.price}`}
              </div>
            )}
          </div>
          <div style={{ marginTop: "2rem", display: "flex", gap: "1rem", justifyContent: "center" }}>
            <Link to="/events" className="btn btn--outline">All Events</Link>
            <Link to="/ops/checkin" className="btn btn--brand">Go to Check-In</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero */}
      <section className="event-detail-hero">
        <div className="container">
          <nav className="event-detail-breadcrumb">
            <Link to="/events">Events</Link>
            <span>›</span>
            <span>{event.title}</span>
          </nav>
          <div className="event-detail-hero-inner">
            <div>
              <span className="eyebrow">{event.dateRange} · {event.location}</span>
              <h1 className="display-1">{event.title}</h1>
              <p className="body-xl text-muted">{event.subtitle}</p>
              <div className="event-detail-tags" style={{ marginTop: "1rem" }}>
                {event.tags.map((tag) => (
                  <span key={tag} className="events-tag-chip">{tag}</span>
                ))}
              </div>
            </div>
            <div className="event-detail-hero-symbol" aria-hidden="true">
              {event.heroEmoji}
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="event-detail-layout">
          {/* Left column — main content */}
          <div className="event-detail-content">
            {/* About section */}
            <section className="event-section">
              <h2 className="event-section-title">About This Event</h2>
              <p className="body-lg">{event.excerpt}</p>
              <p className="body-md text-muted" style={{ marginTop: "1rem" }}>
                Guided by the teachers of Aekam, this retreat offers a rare opportunity
                to step away from daily life and immerse in deep meditation, yoga, satsang,
                and dream exploration practices. You'll leave with practical tools, new
                community connections, and a renewed commitment to the path.
              </p>
            </section>

            {/* What to expect */}
            <section className="event-section">
              <h2 className="event-section-title">What to Expect</h2>
              <div className="event-schedule-blocks">
                <div className="event-schedule-block">
                  <div className="event-schedule-block__day">Day 1 — May 29</div>
                  <ul className="event-schedule-list">
                    <li>Arrival &amp; check-in</li>
                    <li>Opening ceremony &amp; intentions</li>
                    <li>Evening meditation &amp; satsang</li>
                    <li>Community dinner</li>
                  </ul>
                </div>
                <div className="event-schedule-block">
                  <div className="event-schedule-block__day">Day 2 — May 30</div>
                  <ul className="event-schedule-list">
                    <li>Morning yoga &amp; breathwork</li>
                    <li>Astral healing workshop</li>
                    <li>Sacred items fair (AekamMart)</li>
                    <li>Afternoon meditation intensive</li>
                    <li>Evening satsang &amp; kirtan</li>
                  </ul>
                </div>
                <div className="event-schedule-block">
                  <div className="event-schedule-block__day">Day 3 — May 31</div>
                  <ul className="event-schedule-list">
                    <li>Morning silent meditation</li>
                    <li>Closing ceremony &amp; integration</li>
                    <li>Community sharing circle</li>
                    <li>Departure by 1 PM</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Location */}
            <section className="event-section">
              <h2 className="event-section-title">Location</h2>
              <div className="event-location-card">
                <div className="event-location-card__name">📍 {event.location}</div>
                <div className="event-location-card__address">{event.locationDetail}</div>
                <p className="text-muted" style={{ marginTop: "0.5rem", fontSize: "0.9rem" }}>
                  Set within sprawling oak forests and natural springs — a truly sacred setting
                  for inner work. Accommodation options on-site and nearby.
                </p>
              </div>
            </section>

            {/* Sponsors */}
            <section className="event-section">
              <h2 className="event-section-title">Sponsorship Opportunities</h2>
              <p className="text-muted body-md" style={{ marginBottom: "1.5rem" }}>
                Support the event and connect your brand or personal mission with our community.
              </p>
              <div className="sponsor-tiers-grid">
                {SPONSOR_TIERS.map((tier) => (
                  <div key={tier.name} className="sponsor-tier-card">
                    <div className="sponsor-tier-card__emoji">{tier.emoji}</div>
                    <div className="sponsor-tier-card__name">{tier.name}</div>
                    <div className="sponsor-tier-card__price">${tier.price.toLocaleString()}</div>
                    <ul className="sponsor-tier-card__perks">
                      {tier.perks.map((perk, i) => (
                        <li key={i}>{perk}</li>
                      ))}
                    </ul>
                    <a
                    href="mailto:hello@aekam.app?subject=Sponsorship Inquiry"
                      className="btn btn--outline btn--sm"
                      style={{ marginTop: "auto" }}
                    >
                      Inquire
                    </a>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Right column — ticket panel */}
          <div className="event-ticket-panel">
            <div className="event-ticket-widget">
              <div className="event-ticket-widget__header">
                <h3>Register for This Event</h3>
                <div className="event-ticket-widget__date">
                  📅 {event.dateRange}
                </div>
                <div className="event-ticket-widget__loc">
                  📍 {event.location}
                </div>
              </div>

              {registrationStep === "tiers" && (
                <div className="ticket-tiers">
                  {tickets.map((tier) => (
                    <button
                      key={tier.id}
                      type="button"
                      className={`ticket-tier-card${selectedTier === tier.id ? " selected" : ""}${tier.highlight ? " ticket-tier-card--highlight" : ""}`}
                      onClick={() => setSelectedTier(tier.id)}
                    >
                      <div className="ticket-tier-card__top">
                        <div className="ticket-tier-card__name">
                          {tier.name}
                          {tier.highlight && (
                            <span className="ticket-tier-card__popular">Popular</span>
                          )}
                        </div>
                        <div className="ticket-tier-card__price">
                          {tier.price === "pwyc" ? "Pay What You Can" : `$${tier.price}`}
                        </div>
                      </div>
                      <div className="ticket-tier-card__desc">{tier.description}</div>
                      <ul className="ticket-tier-card__perks">
                        {tier.perks.map((perk, i) => (
                          <li key={i} dangerouslySetInnerHTML={{ __html: `✓ ${perk}` }} />
                        ))}
                      </ul>
                    </button>
                  ))}

                  {selectedTier === "pwyc" && (
                    <div className="form-group" style={{ marginTop: "1rem" }}>
                      <label className="form-label" htmlFor="pwyc-amount">
                        Your contribution (USD)
                      </label>
                      <div className="donate-custom-amount">
                        <span className="donate-currency-symbol">$</span>
                        <input
                          id="pwyc-amount"
                          type="number"
                          min="0"
                          className="form-input"
                          placeholder="0 — give what you can"
                          value={pwycAmount}
                          onChange={(e) => setPwycAmount(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn--brand"
                    style={{ width: "100%", marginTop: "1rem" }}
                    disabled={!selectedTier}
                    onClick={() => setRegistrationStep("form")}
                  >
                    {selectedTier ? `Register: ${selectedTicket?.name}` : "Select a Ticket"}
                  </button>
                </div>
              )}

              {registrationStep === "form" && (
                <form className="event-reg-form" onSubmit={handleFormSubmit}>
                  <div className="event-reg-form__back">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setRegistrationStep("tiers")}
                    >
                      ← Change ticket
                    </button>
                    <span className="event-reg-form__tier">
                      {selectedTicket?.name} —{" "}
                      {selectedTicket?.price === "pwyc"
                        ? `$${pwycAmount || "PWYC"}`
                        : `$${selectedTicket?.price}`}
                    </span>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="reg-name">Full Name *</label>
                    <input
                      id="reg-name"
                      type="text"
                      className="form-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your full name"
                      required
                      autoComplete="name"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="reg-email">Email *</label>
                    <input
                      id="reg-email"
                      type="email"
                      className="form-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="reg-dietary">Dietary preferences</label>
                    <input
                      id="reg-dietary"
                      type="text"
                      className="form-input"
                      value={formData.dietary}
                      onChange={(e) => setFormData({ ...formData, dietary: e.target.value })}
                      placeholder="e.g. vegan, gluten-free"
                      autoComplete="off"
                    />
                  </div>
                  <button type="submit" className="btn btn--brand" style={{ width: "100%" }}>
                    Proceed to Confirmation
                  </button>
                </form>
              )}

              {registrationStep === "confirm" && (
                <div className="event-reg-confirm">
                  <h4>Confirm Registration</h4>
                  <div className="event-confirm-details">
                    <div>👤 {formData.name}</div>
                    <div>📧 {formData.email}</div>
                    {formData.dietary && <div>🌱 {formData.dietary}</div>}
                    <div>
                      🎟️ {selectedTicket?.name} —{" "}
                      {selectedTicket?.price === "pwyc"
                        ? `$${pwycAmount || "Your contribution"}`
                        : `$${selectedTicket?.price}`}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn--brand"
                    style={{ width: "100%", marginTop: "1rem" }}
                    onClick={() => setRegistered(true)}
                  >
                    Confirm &amp; Register 🙏
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    style={{ width: "100%", marginTop: "0.5rem" }}
                    onClick={() => setRegistrationStep("form")}
                  >
                    Edit details
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
